type TranslationTree = {
  readonly [key: string]: string | TranslationTree;
};

type TranslationLeaf<T> = T extends string
  ? T
  : T extends Record<string, unknown>
    ? { [K in keyof T]: TranslationLeaf<T[K]> }[keyof T]
    : never;

function flattenTranslationKeys(tree: TranslationTree): string[] {
  return Object.values(tree).flatMap((value) =>
    typeof value === "string" ? value : flattenTranslationKeys(value),
  );
}

export const translationKeys = {
  app: {
    name: "app.name",
    loading: "app.loading",
    error: "app.error",
  },
  auth: {
    signIn: "auth.signIn",
    signOut: "auth.signOut",
    continue: "auth.continue",
  },
  navigation: {
    dashboard: "navigation.dashboard",
    home: "navigation.home",
    clinicianWorkspace: "navigation.clinicianWorkspace",
    patients: "navigation.patients",
    inbox: "navigation.inbox",
    tasks: "navigation.tasks",
    documents: "navigation.documents",
    profile: "navigation.profile",
    settings: "navigation.settings",
  },
  patient: {
    timeline: "patient.timeline",
    upcomingVisits: "patient.upcomingVisits",
    medications: "patient.medications",
    documents: "patient.documents",
  },
  shell: {
    badge: "shell.badge",
    title: "shell.title",
    subtitle: "shell.subtitle",
    statePatient: "shell.statePatient",
    stateScope: "shell.stateScope",
    stateDetailTab: "shell.stateDetailTab",
    workspacePatientsGroup: "shell.workspacePatientsGroup",
    workspaceEngagementGroup: "shell.workspaceEngagementGroup",
    workspaceAiGroup: "shell.workspaceAiGroup",
    workspaceMetricPreOp: "shell.workspaceMetricPreOp",
    workspaceMetricPostOp: "shell.workspaceMetricPostOp",
    workspaceMetricImagingActive: "shell.workspaceMetricImagingActive",
    workspaceMetric60Minutes: "shell.workspaceMetric60Minutes",
    workspaceMetric24Hours: "shell.workspaceMetric24Hours",
    workspaceMetricAiApproved: "shell.workspaceMetricAiApproved",
    workspaceMetricAiEdited: "shell.workspaceMetricAiEdited",
    metricPendingApprovals: "shell.metricPendingApprovals",
    metricEscalations: "shell.metricEscalations",
    workspaceListTitle: "shell.workspaceListTitle",
    workspaceListSubtitle: "shell.workspaceListSubtitle",
    workspaceListMyPatients: "shell.workspaceListMyPatients",
    workspaceListMyPatientsSupport: "shell.workspaceListMyPatientsSupport",
    workspaceListAllPatients: "shell.workspaceListAllPatients",
    workspaceListAllPatientsSupport: "shell.workspaceListAllPatientsSupport",
    workspaceListAttentionOnly: "shell.workspaceListAttentionOnly",
    workspaceListAttentionSupport: "shell.workspaceListAttentionSupport",
    workspaceListSearchPlaceholder: "shell.workspaceListSearchPlaceholder",
    workspaceListVisiblePatients: "shell.workspaceListVisiblePatients",
    workspaceListNoPatients: "shell.workspaceListNoPatients",
    workspaceSelectedPatient: "shell.workspaceSelectedPatient",
    workspacePatientIdentifier: "shell.workspacePatientIdentifier",
    workspaceAssignedToMyPatients: "shell.workspaceAssignedToMyPatients",
    workspaceAddToMyPatients: "shell.workspaceAddToMyPatients",
    workforceSessionLoadingTitle: "shell.workforceSessionLoadingTitle",
    workforceSessionLoadingBody: "shell.workforceSessionLoadingBody",
    workforceSessionSelectionTitle: "shell.workforceSessionSelectionTitle",
    workforceSessionSelectionBody: "shell.workforceSessionSelectionBody",
    workforceSessionErrorBody: "shell.workforceSessionErrorBody",
    workspaceNoPatientSelectedTitle: "shell.workspaceNoPatientSelectedTitle",
    workspaceNoPatientSelectedBody: "shell.workspaceNoPatientSelectedBody",
    workspaceChatTitle: "shell.workspaceChatTitle",
    workspaceChatSubtitle: "shell.workspaceChatSubtitle",
    workspaceChatReviewPendingApproval: "shell.workspaceChatReviewPendingApproval",
    workspaceChatReviewApproved: "shell.workspaceChatReviewApproved",
    workspaceChatReviewApprovedEdited: "shell.workspaceChatReviewApprovedEdited",
    workspaceChatReviewRejected: "shell.workspaceChatReviewRejected",
    workspaceChatConfidence: "shell.workspaceChatConfidence",
    workspaceChatCriticality: "shell.workspaceChatCriticality",
    workspaceChatClinicianNotes: "shell.workspaceChatClinicianNotes",
    workspaceChatReviewActionNotice: "shell.workspaceChatReviewActionNotice",
    workspaceChatDeliverySent: "shell.workspaceChatDeliverySent",
    workspaceChatDeliveryDelivered: "shell.workspaceChatDeliveryDelivered",
    workspaceChatSubmittingBody: "shell.workspaceChatSubmittingBody",
    workspaceActionApprove: "shell.workspaceActionApprove",
    workspaceActionReject: "shell.workspaceActionReject",
    workspaceActionEdit: "shell.workspaceActionEdit",
    workspaceActionSave: "shell.workspaceActionSave",
    workspaceActionSendReply: "shell.workspaceActionSendReply",
    workspaceActionCompleteTask: "shell.workspaceActionCompleteTask",
    workspaceActionMarkDocumentReviewed: "shell.workspaceActionMarkDocumentReviewed",
    workspaceActionRefreshSummary: "shell.workspaceActionRefreshSummary",
    workspaceActionClearAttention: "shell.workspaceActionClearAttention",
    workspaceActionSubmitting: "shell.workspaceActionSubmitting",
    workspaceActionAddToMyPatientsSuccess: "shell.workspaceActionAddToMyPatientsSuccess",
    workspaceActionReplySuccess: "shell.workspaceActionReplySuccess",
    workspaceActionApproveSuccess: "shell.workspaceActionApproveSuccess",
    workspaceActionRejectSuccess: "shell.workspaceActionRejectSuccess",
    workspaceActionCompleteTaskSuccess: "shell.workspaceActionCompleteTaskSuccess",
    workspaceActionMarkDocumentReviewedSuccess:
      "shell.workspaceActionMarkDocumentReviewedSuccess",
    workspaceActionRefreshSummarySuccess:
      "shell.workspaceActionRefreshSummarySuccess",
    workspaceActionPatientRecordSuccess:
      "shell.workspaceActionPatientRecordSuccess",
    workspaceActionClearAttentionSuccess:
      "shell.workspaceActionClearAttentionSuccess",
    workspaceActionError: "shell.workspaceActionError",
    workspaceComposerInputPlaceholder: "shell.workspaceComposerInputPlaceholder",
    workspaceComposerActionNotice: "shell.workspaceComposerActionNotice",
    workspaceComposerScaffoldNotice: "shell.workspaceComposerScaffoldNotice",
    workspaceComposerPlaceholder: "shell.workspaceComposerPlaceholder",
    workspaceDetailsTitle: "shell.workspaceDetailsTitle",
    workspacePanelTitle: "shell.workspacePanelTitle",
    workspacePanelSubtitle: "shell.workspacePanelSubtitle",
    workspacePanelInboxSubtitle: "shell.workspacePanelInboxSubtitle",
    workspacePanelTasksSubtitle: "shell.workspacePanelTasksSubtitle",
    workspacePanelDocumentsSubtitle: "shell.workspacePanelDocumentsSubtitle",
    workspacePanelPatients: "shell.workspacePanelPatients",
    workspacePanelAttention: "shell.workspacePanelAttention",
    workspacePanelUnread: "shell.workspacePanelUnread",
    workspacePanelPendingDocuments: "shell.workspacePanelPendingDocuments",
    workspacePanelEmptyInbox: "shell.workspacePanelEmptyInbox",
    workspacePanelEmptyTasks: "shell.workspacePanelEmptyTasks",
    workspacePanelEmptyDocuments: "shell.workspacePanelEmptyDocuments",
    workspaceDetailInformation: "shell.workspaceDetailInformation",
    workspaceDetailCaseSummary: "shell.workspaceDetailCaseSummary",
    workspaceDetailAppointments: "shell.workspaceDetailAppointments",
    workspaceDetailProcedures: "shell.workspaceDetailProcedures",
    workspaceDetailMedications: "shell.workspaceDetailMedications",
    workspaceDetailDocuments: "shell.workspaceDetailDocuments",
    workspaceDetailTaskAssignments: "shell.workspaceDetailTaskAssignments",
    workspaceDetailApprovals: "shell.workspaceDetailApprovals",
    workspaceDetailDeliveries: "shell.workspaceDetailDeliveries",
    workspaceDetailDocumentsCount: "shell.workspaceDetailDocumentsCount",
    workspaceDetailRowTask: "shell.workspaceDetailRowTask",
    workspaceDetailRowApproval: "shell.workspaceDetailRowApproval",
    workspaceDetailRowDelivery: "shell.workspaceDetailRowDelivery",
    workspaceDetailRowDocument: "shell.workspaceDetailRowDocument",
    workspaceInfoAssignedClinician: "shell.workspaceInfoAssignedClinician",
    workspaceInfoPrimaryProgram: "shell.workspaceInfoPrimaryProgram",
    workspaceInfoNextAppointment: "shell.workspaceInfoNextAppointment",
    workspaceInfoContactWindow: "shell.workspaceInfoContactWindow",
    workspaceDocumentCreatedBy: "shell.workspaceDocumentCreatedBy",
    workspaceDocumentCreatedAt: "shell.workspaceDocumentCreatedAt",
    workspaceDocumentDownloadReady: "shell.workspaceDocumentDownloadReady",
    workspaceDocumentMetadataOnly: "shell.workspaceDocumentMetadataOnly",
    workspaceAppointmentRow: "shell.workspaceAppointmentRow",
    workspaceProcedureRow: "shell.workspaceProcedureRow",
    workspaceMedicationRow: "shell.workspaceMedicationRow",
    workspaceDocumentRow: "shell.workspaceDocumentRow",
    workspaceStatusNeedsReply: "shell.workspaceStatusNeedsReply",
    workspaceStatusReviewReady: "shell.workspaceStatusReviewReady",
    workspaceStatusAwaitingDocument: "shell.workspaceStatusAwaitingDocument",
    workspacePatientOne: "shell.workspacePatientOne",
    workspacePatientTwo: "shell.workspacePatientTwo",
    workspacePatientThree: "shell.workspacePatientThree",
    workspaceSummaryPatientOne: "shell.workspaceSummaryPatientOne",
    workspaceSummaryPatientTwo: "shell.workspaceSummaryPatientTwo",
    workspaceSummaryPatientThree: "shell.workspaceSummaryPatientThree",
    workspaceMessagePatientSender: "shell.workspaceMessagePatientSender",
    workspaceMessageClinicianSender: "shell.workspaceMessageClinicianSender",
    workspaceMessageSystemSender: "shell.workspaceMessageSystemSender",
    workspaceMessagePatientBodyOne: "shell.workspaceMessagePatientBodyOne",
    workspaceMessageClinicianBodyOne: "shell.workspaceMessageClinicianBodyOne",
    workspaceMessageSystemBodyOne: "shell.workspaceMessageSystemBodyOne",
    workspaceMessagePatientBodyTwo: "shell.workspaceMessagePatientBodyTwo",
    workspaceMessageClinicianBodyTwo: "shell.workspaceMessageClinicianBodyTwo",
    workspaceMessageSystemBodyTwo: "shell.workspaceMessageSystemBodyTwo",
    workspaceMessagePatientBodyThree: "shell.workspaceMessagePatientBodyThree",
    workspaceMessageClinicianBodyThree: "shell.workspaceMessageClinicianBodyThree",
    workspaceMessageSystemBodyThree: "shell.workspaceMessageSystemBodyThree",
    flagPreOp: "shell.flagPreOp",
    flagPostOp: "shell.flagPostOp",
    flagImagingActive: "shell.flagImagingActive",
    flagPreAdmission: "shell.flagPreAdmission",
    flagChronicFollowUp: "shell.flagChronicFollowUp",
  },
  settings: {
    language: "settings.language",
    notifications: "settings.notifications",
  },
} as const satisfies TranslationTree;

export const translationKeyList = flattenTranslationKeys(translationKeys) as readonly TranslationLeaf<
  typeof translationKeys
>[];

export type TranslationKey = TranslationLeaf<typeof translationKeys>;
